import { FetchAPI } from '../Main/fetchAPI';
import { CLIENT } from '../constants';

const SCHEDULE_CANCELLATIONS = `${CLIENT}/schedule-cancellations`;

// --- Enums (mirror BE values) ---

export enum CancellingParty {
	Provider = 0,
	ClientFamily = 1,
	Agency = 2,
}

export enum CancellationReason {
	// Single-session
	ProviderSick = 0,
	ProviderEmergency = 1,
	ProviderOther = 2,
	ClientSick = 3,
	ClientVacation = 4,
	ClientEmergency = 5,
	ClientOther = 6,
	// Extended cancellation (deprecated — superseded by party-specific reasons below)
	Vacation = 7,
	Illness = 8,
	FamilyEmergency = 9,
	Medical = 10,
	Other = 11,
	// Extended cancellation — Provider (BT)
	ExtendedProviderVacation = 12,
	ExtendedProviderIllness = 13,
	ExtendedProviderMedical = 14,
	ExtendedProviderOther = 15,
	// Extended cancellation — Client/family
	ExtendedClientVacation = 16,
	ExtendedClientEmergency = 17,
	ExtendedClientMedical = 18,
	ExtendedClientOther = 19,
}

export enum RescheduleIntent {
	Yes = 0,
	No = 1,
	NotSure = 2,
}

export enum CancellationStatus {
	Applied = 0,
	Pending = 1,
	Approved = 2,
	Denied = 3,
}

export enum CancellationType {
	SingleSession = 0,
	ExtendedTimeOff = 1,
}

export enum CancellationOrigin {
	ProviderRequested = 0,
	CaseManagerEntered = 1,
}

// --- Label maps for dropdowns ---

export const providerReasonOptions = [
	{ id: CancellationReason.ProviderSick, label: 'BT is sick/unwell' },
	{ id: CancellationReason.ProviderEmergency, label: 'BT has a personal emergency' },
	{ id: CancellationReason.ProviderOther, label: 'BT - Other reason' },
];

export const clientReasonOptions = [
	{ id: CancellationReason.ClientSick, label: 'Client is sick/unwell' },
	{ id: CancellationReason.ClientVacation, label: 'Client/family on vacation' },
	{ id: CancellationReason.ClientEmergency, label: 'Client/family emergency' },
	{ id: CancellationReason.ClientOther, label: 'Client/family - Other reason' },
];

export const singleSessionReasonOptions = [...providerReasonOptions, ...clientReasonOptions];

export const extendedProviderReasonOptions = [
	{ id: CancellationReason.ExtendedProviderVacation, label: 'BT vacation / personal travel' },
	{ id: CancellationReason.ExtendedProviderIllness, label: 'BT extended illness' },
	{ id: CancellationReason.ExtendedProviderMedical, label: 'BT medical appointment / procedure' },
	{ id: CancellationReason.ExtendedProviderOther, label: 'BT - Other reason' },
];

export const extendedClientReasonOptions = [
	{ id: CancellationReason.ExtendedClientVacation, label: 'Client/family vacation' },
	{ id: CancellationReason.ExtendedClientEmergency, label: 'Client/family emergency' },
	{ id: CancellationReason.ExtendedClientMedical, label: 'Client/family medical appointment / procedure' },
	{ id: CancellationReason.ExtendedClientOther, label: 'Client/family - Other reason' },
];

export const rescheduleIntentOptions = [
	{ id: RescheduleIntent.Yes, label: 'Yes - I will coordinate a makeup session' },
	{ id: RescheduleIntent.No, label: 'No - This session will not be made up' },
	{ id: RescheduleIntent.NotSure, label: 'Not sure yet' },
];

// --- Request payloads ---

export interface CreateSingleSessionCancellationPayload {
	cancellingParty: CancellingParty;
	date: string;
	notes?: string;
	reasonId?: number;
	recurringScheduleId: string;
	recurringScheduleTimeSlotId?: string | null;
	rescheduleIntent?: RescheduleIntent;
}

export interface CreateCancellationRequestPayload {
	allClients: boolean;
	cancellingParty: CancellingParty;
	clientIds: string[];
	endDate: string;
	notes?: string;
	providerId: string;
	reasonId?: number;
	recurringScheduleId?: string;
	startDate: string;
}

export interface CancellationDecisionPayload {
	decisionNotes?: string;
}

// --- Response types ---

export interface ScheduleCancellationViewModel {
	branchId: string | null;
	cancellingParty: CancellingParty | null;
	clientId: string | null;
	clientName: string | null;
	isElc: boolean;
	createdAt: string;
	decisionNotes: string | null;
	endDate: string;
	groupId: string | null;
	id: string;
	notes: string | null;
	origin: CancellationOrigin;
	providerId: string;
	providerName: string;
	reasonId: number | null;
	reasonName: string | null;
	recurringScheduleId: string | null;
	recurringScheduleTimeSlotId?: string | null;
	rescheduleIntent: RescheduleIntent | null;
	reviewedAt: string | null;
	reviewedById: string | null;
	reviewedByName: string | null;
	startDate: string;
	status: CancellationStatus;
	type: CancellationType;
}

export interface AffectedClientViewModel {
	clientId: string;
	clientName: string;
	isElc: boolean;
}

export interface PendingCancellationRequestViewModel {
	affectedClients: AffectedClientViewModel[];
	affectedSessionCount: number;
	allClients: boolean;
	cancellingParty: CancellingParty | null;
	createdAt: string;
	endDate: string;
	groupId: string;
	notes: string | null;
	providerId: string;
	providerName: string;
	reasonId: number | null;
	reasonName: string | null;
	rescheduleIntent: RescheduleIntent | null;
	startDate: string;
}

// --- API functions ---

export const ScheduleCancellation = {
	createSingleSession: (data: CreateSingleSessionCancellationPayload) =>
		FetchAPI.postJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/single`, data }),

	createCancellationRequest: (data: CreateCancellationRequestPayload) =>
		FetchAPI.postJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/cancellation-request`, data }),

	getCancellations: (params: { branchId?: string; clientId?: string; endDate?: string; providerId?: string; startDate?: string }) => {
		const query = new URLSearchParams();
		if (params.providerId) query.set('providerId', params.providerId);
		if (params.clientId) query.set('clientId', params.clientId);
		if (params.startDate) query.set('startDate', params.startDate);
		if (params.endDate) query.set('endDate', params.endDate);
		if (params.branchId) query.set('branchId', params.branchId);
		const qs = query.toString();

		return FetchAPI.getJson({ urlStr: `${SCHEDULE_CANCELLATIONS}${qs ? `?${qs}` : ''}` });
	},

	getCancellationsForCalendar: (params: { branchId?: string; clientIds?: string[]; endDate: string; providerIds: string[]; startDate: string }) => {
		const query = new URLSearchParams();
		params.providerIds.forEach((id) => query.append('providerIds', id));
		query.set('startDate', params.startDate);
		query.set('endDate', params.endDate);
		params.clientIds?.forEach((id) => query.append('clientIds', id));
		if (params.branchId) query.set('branchId', params.branchId);

		return FetchAPI.getJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/calendar?${query.toString()}` });
	},

	getPendingRequests: (branchId?: string) => {
		const query = new URLSearchParams();
		if (branchId) query.set('branchId', branchId);
		const qs = query.toString();

		return FetchAPI.getJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/pending${qs ? `?${qs}` : ''}` });
	},

	approveGroup: (groupId: string) =>
		FetchAPI.postJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/group/${groupId}/approve` }),

	denyGroup: (groupId: string, data?: CancellationDecisionPayload) =>
		FetchAPI.postJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/group/${groupId}/deny`, data: data ?? {} }),

	deleteCancellation: (id: string) =>
		FetchAPI.deleteJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/${id}` }),

	deleteGroup: (groupId: string) =>
		FetchAPI.deleteJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/group/${groupId}` }),

	undoCancellation: (id: string) =>
		FetchAPI.postJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/${id}/undo` }),

	undoGroup: (groupId: string) =>
		FetchAPI.postJson({ urlStr: `${SCHEDULE_CANCELLATIONS}/group/${groupId}/undo` }),
};
